<template>
  <div class="workers-screen">
    <div class="row text-right mt-5 mr-20" v-if="loggedInUser.role === 'admin'">
      <base-button
        :title="$t('app.generals.createNew')"
        classNames="btn btn-orange rounded pull-right"
        @clicked="clickedCreateWorker"
      />
    </div>
    <configurable-list
      v-if="model && model.users"
      :data="model.users"
      :loadingText="loadingText"
      :fields="this.loggedInUser.role === 'admin'?  adminFields : fields"
      @actionMade="actionMade"
      @orderTable="orderTable"
    />
    <paginator-component
      :totalPages="totalPages"
      :currentPageIndex="currentPageIndex"
      @itemSelected="itemSelected"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ConfigurableList from '@/components/shared/lists/ConfigurableList';
import Paginator from '@/components/shared/navigations/Paginator';
import BaseButton from '@/components/shared/buttons/BaseButton';
import GenericForm from '@/components/shared/forms/GenericForm';

export default {
  name: 'WorkersScreen',
  components: {
    'configurable-list': ConfigurableList,
    'paginator-component': Paginator,
    'base-button': BaseButton,
  },
  props: {
    model: {
      type: [Object, Array],
      required: true,
    },
  },
  data() {
    return {
      order: undefined,
      direction: undefined,
      roles: ['worker', 'vivu'],
      currentPageIndex: 1,
      searchTerm: '',
      newWorker: {
        name: undefined,
        user_name: undefined,
        email: undefined,
        phone: undefined,
        job: undefined,
        role: undefined,
        note: undefined,
      },
    };
  },
  created() {

  },
  computed: {
    totalPages() {
      return this.model.total_pages || 0;
    },
    ...mapGetters({
      loggedInUser: 'loggedInUser',
      partner: 'partners/selectedPartner',
    }),
    fields() {
      return [
        {
          title: this.$t('app.tables.workers.name'),
          cellClass: 'name-cell',
          key: 'name',
          orderable: true,
        },
        {
          title: this.$t('app.tables.workers.userName'),
          cellClass: 'name-cell',
          key: 'user_name',
          orderable: true,
        },
        {
          title: this.$t('app.tables.workers.job'),
          cellClass: 'email-cell',
          key: 'job',
          orderable: true,
        },
        {
          title: this.$t('app.tables.workers.email'),
          cellClass: 'email-cell',
          key: 'email',
          orderable: true,
        },
        {
          title: this.$t('app.tables.workers.phone'),
          cellClass: 'phone-cell',
          key: 'phone',
          orderable: true,
        },
        {
          title: this.$t('app.tables.workers.role'),
          cellClass: 'job-cell',
          key: 'role',
          withTranslation: true,
          translation: 'app.roles',
          orderable: true,
        },
      ];
    },
    adminFields() {
      return [
        {
          title: this.$t('app.tables.workers.name'),
          cellClass: 'name-cell',
          key: 'name',
          orderable: true,
        },
        {
          title: this.$t('app.tables.workers.userName'),
          cellClass: 'name-cell',
          key: 'user_name',
          orderable: true,
        },
        {
          title: this.$t('app.tables.workers.job'),
          cellClass: 'job-cell',
          key: 'job',
          orderable: true,
        },
        {
          title: this.$t('app.tables.workers.email'),
          cellClass: 'email-cell',
          key: 'email',
          orderable: true,
        },
        {
          title: this.$t('app.tables.workers.phone'),
          cellClass: 'phone-cell',
          key: 'phone',
          orderable: true,
        },
        {
          title: this.$t('app.tables.workers.role'),
          cellClass: 'job-cell',
          key: 'role',
          withTranslation: true,
          translation: 'app.roles',
          orderable: true,
        },
        {
          title: this.$t('app.tables.global.actions'),
          isAction: true,
          cellClass: 'edit-cell-admin',
          key: 'edit',
          class: 'zmdi zmdi-edit',
        },
        {
          title: '',
          isAction: true,
          cellClass: 'delete-cell-admin',
          key: 'delete',
          class: 'zmdi zmdi-delete',
        },
      ];
    },
    workerAttributes() {
      return [
        {
          name: 'name',
          type: 'text',
          classes: 'col-md-6',
        },
        {
          name: 'job',
          type: 'text',
          classes: 'col-md-6',
        },
        {
          name: 'role',
          type: 'dropdown',
          dropdownList: this.workerRoles,
          classes: 'col-md-6',
        },
        {
          name: 'email',
          type: 'text',
          classes: 'col-md-6',
        },
        {
          name: 'phone',
          type: 'text',
          classes: 'col-md-6 without-float',
        },
        {
          name: 'note',
          type: 'text-area',
          classes: 'col-md-12',
        },
      ];
    },
    workerRoles() {
      return [
        {
          text: this.$t('app.mailRoles.vivu'),
          value: 'vivu',
        },
        {
          text: this.$t('app.mailRoles.worker'),
          value: 'worker',
        },
      ];
    },
    loadingText() {
      if (this.model && this.model.users.length === 0) {
        return this.$t('app.tasks.empty_list');
      }
      return this.$t('app.listLoading');
    },
  },
  methods: {
    ...mapActions({
      changeUser: 'users/changeUser',
      fetchWorkers: 'users/fetchWorkers',
      fetchWorker: 'users/fetchWorker',
      createNewWorker: 'users/createWorker',
      updateWorkerParams: 'users/updateWorker',
      deleteWorker: 'users/deleteWorker',
      fetchCommonData: 'partners/fetchCommonData',
    }),
    itemSelected(index) {
      this.currentPageIndex = index;
    },
    clickedCreateWorker() {
      this.$eventBus.showModal({
        bind: {
          is: GenericForm,
          title: this.$t('app.users.set_worker'),
          fields: this.workerAttributes,
          model: this.newWorker,
          submitTitle: this.$t('app.users.buttons.save'),
          cancelTitle: this.$t('app.users.buttons.cancel'),
          modelName: 'users',
        },
        on: {
          submit: this.createWorker,
          modalCanceled: this.resetNewWorker,
        },
        width: '1000px',
      });
    },
    createWorker(newWorker) {
      this.createNewWorker({
        partnerId: this.loggedInUser.partner_id,
        siteId: this.$route.params.id,
        params: newWorker,
      })
        .then(() => {
          this.$eventBus.showNotification({
            notificationText: this.$t('app.users.success.create'),
            notificationType: 'success',
          });
          this.fetchWorkers({
            partnerId: this.loggedInUser.partner_id,
            siteId: this.$route.params.id,
            pageIndex: this.currentPageIndex,
            searchTerm: this.searchTerm,
          }).then(() => {
            this.resetNewWorker();
            this.$eventBus.hideModal();
          });
        })
        .catch((error) => {
          this.$eventBus.stopLoading();
          const mappedErrors = error.map(x => ` ${x}`);
          this.$eventBus.getError(mappedErrors);
        });
    },
    resetNewWorker() {
      Object.assign(this.$data.newWorker, this.$options.data().newWorker);
    },
    updateworker(worker) {
      this.updateWorkerParams({
        partnerId: this.loggedInUser.partner_id,
        siteId: this.$route.params.id,
        userId: worker.id,
        params: worker,
      })
        .then((response) => {
          this.$eventBus.showNotification({
            notificationText: this.$t('app.users.success.update'),
            notificationType: 'success',
          });
          if (this.loggedInUser.id === response.id) {
            this.loggedInUser.name = response.name;
          }
          this.fetchWorkers({
            partnerId: this.loggedInUser.partner_id,
            siteId: this.$route.params.id,
            pageIndex: this.currentPageIndex,
            searchTerm: this.searchTerm,
            roles: this.roles,
          })
            .then(() => {
              this.$eventBus.hideModal();
            })
            .catch((error) => {
              this.$swal
                .fire({
                  html: error[0],
                  type: 'error',
                  showCloseButton: true,
                  showCancelButton: false,
                  showConfirmButton: false,
                  focusConfirm: false,
                });
            });
        })
        .catch((error) => {
          this.$eventBus.stopLoading();
          const mappedErrors = error.map(x => ` ${x}`);
          this.$eventBus.getError(mappedErrors);
        });
    },
    actionMade(payload) {
      if (payload.actionType === 'edit') {
        this.fetchWorker({
          partnerId: this.loggedInUser.partner_id,
          siteId: this.$route.params.id,
          userId: payload.id,
        })
          .then((worker) => {
            this.$eventBus.showModal({
              bind: {
                is: GenericForm,
                title: this.$t('app.users.edit_worker'),
                fields: this.workerAttributes,
                model: worker,
                submitTitle: this.$t('app.users.buttons.save'),
                cancelTitle: this.$t('app.users.buttons.cancel'),
                modelName: 'users',
              },
              on: {
                submit: this.updateworker,
              },
              width: '1000px',
            });
          })
          .catch((error) => {
            const mappedErrors = error.map(x => ` ${x}`);
            const errorMessages = this.$t('app.errors.general_validation', {
              message: mappedErrors,
            });
            this.$swal
              .fire({
                html: errorMessages,
                type: 'error',
                showCloseButton: true,
                showCancelButton: false,
                showConfirmButton: false,
                focusConfirm: false,
              });
          });
      } else if (payload.actionType === 'delete') {
        this.$swal
          .fire({
            html: this.$t('app.notifications.general_confirm.title'),
            type: 'info',
            showCloseButton: true,
            showCancelButton: true,
            focusConfirm: false,
            confirmButtonText: this.$t('app.notifications.general_confirm.ok'),
            cancelButtonText: this.$t('app.notifications.general_confirm.cancel'),
          })
          .then((result) => {
            if (result.value) {
              this.deleteWorker({
                partnerId: this.loggedInUser.partner_id,
                siteId: this.$route.params.id,
                userId: payload.id,
              })
                .then(() => {
                  this.$eventBus.showNotification({
                    notificationText: this.$t('app.users.success.delete'),
                    notificationType: 'success',
                  });
                  this.fetchWorkers({
                    partnerId: this.loggedInUser.partner_id,
                    siteId: this.$route.params.id,
                    pageIndex: this.currentPageIndex,
                    searchTerm: this.searchTerm,
                    roles: this.roles,
                  });
                })
                .catch((error) => {
                  this.$swal
                    .fire({
                      html: error[0],
                      type: 'error',
                      showCloseButton: true,
                      showCancelButton: false,
                      showConfirmButton: false,
                      focusConfirm: false,
                    });
                });
            }
          });
      } else if (payload.actionType === 'changeUser') {
        this.$swal
          .fire({
            html: this.$t('app.notifications.general_confirm.changeTitle'),
            type: 'info',
            showCloseButton: true,
            showCancelButton: true,
            focusConfirm: false,
            confirmButtonText: this.$t('app.notifications.general_confirm.ok'),
            cancelButtonText: this.$t('app.notifications.general_confirm.cancel'),
          })
          .then((result) => {
            if (result.value) {
              this.changeUser(payload.id).then((response) => {
                this.fetchCommonData(response.id).then(() => {
                  this.$router.push({ name: 'DashboardScreen' });
                });
              });
            }
          });
      }
    },
    orderTable(payload) {
      this.order = payload.order;
      this.direction = payload.direction;
      this.fetchWorkers({
        partnerId: this.loggedInUser.partner_id,
        siteId: this.$route.params.id,
        pageIndex: this.currentPageIndex,
        searchTerm: this.searchTerm,
        roles: this.roles,
        orderInfo: {
          order: payload.order,
          direction: payload.direction,
        },
      });
    },
  },
  watch: {
    currentPageIndex(newValue) {
      if (this.order !== undefined && this.direction !== undefined) {
        this.fetchWorkers({
          partnerId: this.loggedInUser.partner_id,
          siteId: this.$route.params.id,
          pageIndex: newValue,
          searchTerm: this.searchTerm,
          roles: this.roles,
          orderInfo: {
            order: this.order,
            direction: this.direction,
          },
        });
      } else {
        this.fetchWorkers({
          partnerId: this.loggedInUser.partner_id,
          siteId: this.$route.params.id,
          pageIndex: newValue,
          searchTerm: this.searchTerm,
          roles: this.roles,
        });
      }
    },
  },
};
</script>

<style lang="scss">
.workers-screen {
  /deep/ .configurable-list {
    .card-view {
      .panel-heading {
        .row {
          margin-right: 50%;
        }
        margin-top: -70px;
      }
    }
  }
  .rounded {
    border-radius: 4px;
  }
  .name-cell {
  width: 14%;
  }

  .user-name-cell {
    width: 15%;
  }

  .email-cell {
    width: 15%;
  }

  .phone-cell {
    width: 10%;
  }

  .job-cell {
    width: 10%;
  }

  .remote-cell {
    width: 2%;
  }

  .edit-cell {
    width: 2.5%;
  }

  .edit-cell-admin {
    width: 3.5%;
  }

  .delete-cell-admin {
    width: 3.5%;
  }

  .delete-cell {
    width: 5.5%;
  }

  .without-float {
    clear: left;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/modules/partners/WorkersScreen.vue